import { useEffect } from "react";
import { Link } from "react-router-dom";
import { agingTypesPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

export default function AgingTypes() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="老化型態分析｜找出你的變老類型｜時光逆行駭客"
        description="你是哪一種正在變老？從男性懸崖式衰老、過勞上班族、美肌控到熟齡族與長青族，解析五種老化型態。認識自己的老化軌跡，才能對症設計逆齡方案。"
        keywords="老化型態,變老類型,懸崖式衰老,過勞老化,時光逆行駭客,逆齡診斷"
        canonicalPath="/aging-types"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "老化型態分析｜時光逆行駭客",
          "description": "從五種老化型態認識自己的老化軌跡，對症設計逆齡方案。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{agingTypesPageData.heroTagline}</span>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold text-ghost leading-tight">
              {agingTypesPageData.heroTitle}
              <br className="hidden md:block" />
              <span className="text-gradient-gold">{agingTypesPageData.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              {agingTypesPageData.heroDesc}
            </p>
          </div>
        </div>
      </section>

      {/* Type Cards */}
      <section className="py-16 md:py-24">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="space-y-8 max-w-4xl mx-auto">
            {agingTypesPageData.types.map((type, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 reveal"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                {/* Header row */}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${type.accent}15` }}
                    >
                      <i className={`${type.icon} text-xl`} style={{ color: type.accent }} />
                    </div>
                    <div>
                      <span
                        className="text-base font-mono font-bold tracking-wider"
                        style={{ color: type.accent }}
                      >
                        {type.code}
                      </span>
                      <h3 className="text-ghost font-bold text-lg mt-0.5">{type.title}</h3>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="px-3 py-1 rounded-full text-base font-medium bg-abyss-800 text-ghost-muted border border-abyss-700">
                      {type.gender}
                    </span>
                    <span className="px-3 py-1 rounded-full text-base font-medium bg-abyss-800 text-ghost-muted border border-abyss-700">
                      {type.ageRange}
                    </span>
                  </div>
                </div>

                {/* Symptoms */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {type.symptoms.map((s, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 rounded-md text-base font-medium border"
                      style={{
                        color: type.accent,
                        borderColor: `${type.accent}30`,
                        backgroundColor: `${type.accent}08`,
                      }}
                    >
                      {s}
                    </span>
                  ))}
                </div>

                {/* Scenario */}
                <div className="bg-abyss-900/60 border border-abyss-700 rounded-lg p-4 md:p-5 mb-6">
                  <p className="text-ghost-muted text-base leading-relaxed italic">
                    「{type.scenario}」
                  </p>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-ghost-dim text-base">{type.cta}</p>
                  <Link
                    to="/booking"
                    className="btn-gold text-base whitespace-nowrap shrink-0"
                  >
                    預約逆齡評估
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{agingTypesPageData.bottomCta.label}</span>
            <h2 className="mt-4 text-2xl md:text-4xl font-bold text-ghost">
              {agingTypesPageData.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={lineUrl} target="_blank" rel="noopener noreferrer" className="btn-line whitespace-nowrap">
                <i className="ri-line-fill" />
                {agingTypesPageData.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold whitespace-nowrap">
                {agingTypesPageData.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}